import { yupResolver } from "@hookform/resolvers/yup";
import { Flex, Form, Image, Link, Text } from "@wayflyer/flyui";
import { RhfTextField } from "@wayflyer/flyui-hook-form";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import type { MessageDescriptor } from "react-intl";
import { defineMessage, useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";
import * as Yup from "yup";

import { AuthCallbackPath } from "../../callback-paths";
import { usePlatformConnectorProperties } from "../../queries";
import { connectPostPath, useConnectionFlowsConfig } from "../../config";
import type { MockifyData } from "../../common/connect-by-post";
import { useConnectByPost } from "../../common/connect-by-post";
import { ConnectionsList } from "../../common/connections";
import { OAuthErrorBanner } from "../../common/oauth-error-banner";
import { MESSAGES } from "../../messages";

const DEFAULT_URL_HINT_ERROR = defineMessage({
  id: "funding_application: valid mockify URL hint",
  defaultMessage: "Please enter a valid Mockify store url.",
});

const MOCKIFY_SERVER_VALIDATION_ERRORS: Map<string, MessageDescriptor> = new Map([
  ["valid URL hint", DEFAULT_URL_HINT_ERROR],
]);

const CONNECTOR_SLUG = "mockify";

interface MockifyConnectProps {
  postConnectPath: AuthCallbackPath;
  onInitiateConnection?: () => void;
}

export const MockifyConnect = ({ postConnectPath, onInitiateConnection }: MockifyConnectProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { connectorsApiUrl } = useConnectionFlowsConfig();
  const { data: connectorProperties } = usePlatformConnectorProperties(
    customerDomain,
    CONNECTOR_SLUG,
    postConnectPath,
    connectorsApiUrl
  );

  const connectByPost = useConnectByPost();

  const schema = Yup.object({
    storeName: Yup.string()
      .trim()
      .required(intl.formatMessage(DEFAULT_URL_HINT_ERROR))
      .transform((value: string) => {
        if (value.startsWith("http://") || value.startsWith("https://")) {
          return value;
        } else {
          return "https://" + value;
        }
      })
      .url(intl.formatMessage(DEFAULT_URL_HINT_ERROR)),
  });

  const mockifyForm = useForm<MockifyData>({
    resolver: yupResolver(schema),
  });

  const connectHandler = async (formData: MockifyData) => {
    const postPath = connectPostPath(customerDomain, CONNECTOR_SLUG, postConnectPath);
    if (postPath) {
      onInitiateConnection?.();
      const response = await connectByPost(postPath, formData);
      if (response?.errorMessage && response?.errorField) {
        if (response.errorField === "storeName") {
          mockifyForm.clearErrors();
          mockifyForm.setError("storeName", {
            type: "validate",
            message: intl.formatMessage(
              MOCKIFY_SERVER_VALIDATION_ERRORS.get(response.errorMessage) ?? DEFAULT_URL_HINT_ERROR
            ),
          });
        }
      }
    }
  };

  return (
    <Flex direction="column" justify="flex-start">
      <Flex direction="column" gap="8">
        <OAuthErrorBanner connectorSlug={CONNECTOR_SLUG} />
        <Text>{intl.formatMessage(MESSAGES.ecommerceDataUsage, { platform: "Mockify\n(internal only)" })}</Text>
        <ConnectionsList
          connectorProperties={connectorProperties}
          renderConnectAnother={(onConnectAnother) => (
            <Text size="sm">
              {intl.formatMessage(MESSAGES.connectAnotherStore, {
                link: (chunks: ReactNode) => <Link onClick={onConnectAnother}>{chunks}</Link>,
              })}
            </Text>
          )}
          connectForm={
            <Form
              onSubmit={mockifyForm.handleSubmit(connectHandler)}
              primaryAction={{
                content: intl.formatMessage({
                  id: "funding-application: log in to mockify",
                  defaultMessage: "Connect Mockify",
                }),
                loading: mockifyForm.formState.isSubmitting,
              }}
            >
              <RhfTextField
                label={intl.formatMessage({
                  id: "funding-application: enter mockify store url",
                  defaultMessage: "Enter store URL",
                })}
                labelIcon={
                  connectorProperties.connectionLogoSrc ? (
                    <Image src={connectorProperties.connectionLogoSrc} />
                  ) : undefined
                }
                name="storeName"
                valuePrefix="https://"
                form={mockifyForm}
                disabled={mockifyForm.formState.isSubmitting}
              />
            </Form>
          }
        />
      </Flex>
    </Flex>
  );
};
